// src/pages/fleet/master/MasterTable.tsx — shared list/CRUD screen for every Master module.
// One component so Assets / Vehicle / State / District look and behave identically:
// stat chips, search, filters, sortable columns, pagination, add/edit drawer,
// active toggle, CSV export, and a card layout on phones.
import React, { useMemo, useState } from 'react';
import { P, css } from '../fleetStyles';
import useIsMobile from '../useIsMobile';
import Icon, { IconName } from '../Icon';

const PAGE_SIZES = [10, 25, 50];

/** Values a master form field can hold (string[] = a `checkboxes` field). */
export type FieldValue = string | number | boolean | string[] | null | undefined;
export type FormValues = Record<string, FieldValue>;
export type Option = string | { value: string; label: string; hint?: string };

export interface Column<R> {
  /** Row property to show/sort by; may also name a computed column that uses `render`. */
  key: string;
  label: string;
  width?: number;
  sortable?: boolean;
  csv?: boolean;
  render?: (row: R) => React.ReactNode;
  csvValue?: (row: R) => FieldValue;
}

export interface Field {
  key: string;
  label: string;
  type?: 'text' | 'number' | 'date' | 'select' | 'toggle' | 'email' | 'password' | 'checkboxes';
  required?: boolean;
  placeholder?: string;
  hint?: string;
  options?: Option[];
  default?: FieldValue;
  /** Field can be set when creating but not changed when editing (e.g. IMEI). */
  lockOnEdit?: boolean;
  /** Required when adding, may be left blank when editing (e.g. password). */
  optionalOnEdit?: boolean;
  /** Wording beside a toggle, when "Active / Inactive" is not what it means. */
  onLabel?: string;
  offLabel?: string;
  /** Only shown when editing (e.g. an Active toggle). */
  editOnly?: boolean;
  /** Take half the drawer width, so two such fields sit side by side. */
  half?: boolean;
  /** Hide this field for a particular row (receives null when adding). */
  hidden?: (form: FormValues, existing: unknown) => boolean;
}

export interface Filter { key: string; label: string; options: Option[] }
export interface Stat { label: string; value: React.ReactNode; tint: string }

export interface MasterTableProps<R> {
  icon: IconName;
  title: string;
  subtitle: string;
  sample?: boolean;
  /** A note shown above the table, in place of the "Sample data" warning. */
  banner?: React.ReactNode;
  rows: R[];
  idKey?: string;
  columns: Column<R>[];
  fields: Field[];
  searchKeys?: string[];
  filters?: Filter[];
  stats?: Stat[];
  /** `existing` is the row being edited, or null when adding. */
  onSave?: (form: FormValues, existing: R | null) => Promise<void>;
  onToggle?: (row: R) => void;
  /** Delete a row (after the user confirms); throw to show an error. */
  onDelete?: (row: R) => Promise<void>;
  /** Hide the edit / delete buttons for particular rows. */
  canEdit?: (row: R) => boolean;
  canDelete?: (row: R) => boolean;
  /** Name shown in the delete confirmation. */
  rowLabel?: (row: R) => string;
  /** Build the drawer form from a row (defaults to a copy of the row). */
  toForm?: (row: R) => FormValues;
  /** Extra checks before saving; return an error message to block the save. */
  validate?: (form: FormValues, existing: R | null) => string | null;
  drawerWidth?: number;
  statusKey?: string;
  /** Filter values to start with, e.g. from the URL (?status=IDLE). */
  initialFilters?: Record<string, string>;
  addLabel?: string;
  emptyText?: string;
}

const optValue = (o: Option) => (typeof o === 'string' ? o : o.value);
const optLabel = (o: Option) => (typeof o === 'string' ? o : o.label);
/** Read any column off a row; columns are declared by string key. */
const valueOf = <R,>(row: R, key: string): unknown => (row as unknown as Record<string, unknown>)[key];

function errorText(e: unknown): string {
  const ax = e as { response?: { data?: { error?: string } }; message?: string };
  return ax?.response?.data?.error || ax?.message || 'Could not save';
}

export default function MasterTable<R extends object>({
  icon, title, subtitle, sample = false, banner,
  rows, idKey = 'id', columns, fields, searchKeys = [],
  filters = [], stats = [], onSave, onToggle, onDelete, canEdit, canDelete, rowLabel, toForm, validate,
  drawerWidth = 420, statusKey = 'active', initialFilters,
  addLabel = 'Add', emptyText = 'No records yet',
}: MasterTableProps<R>) {
  const isMobile = useIsMobile();
  const [q, setQ] = useState('');
  const [filterVals, setFilterVals] = useState<Record<string, string>>(initialFilters || {});
  const [sort, setSort] = useState<{ key: string | null; dir: 1 | -1 }>({ key: null, dir: 1 });
  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(1);
  // null = drawer closed; { row: null } = adding; { row } = editing that row
  const [editing, setEditing] = useState<{ row: R | null } | null>(null);
  const [form, setForm] = useState<FormValues>({});
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState(false);
  const [confirmDel, setConfirmDel] = useState<R | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [notice, setNotice] = useState<{ tone: 'ok' | 'err'; text: string } | null>(null);
  const isEdit = !!editing?.row;
  const drawerFields = fields.filter((f) => (!f.editOnly || isEdit) && !(f.hidden && f.hidden(form, editing?.row ?? null)));
  const editable = (r: R) => !!onSave && (!canEdit || canEdit(r));
  const deletable = (r: R) => !!onDelete && (!canDelete || canDelete(r));
  const hasActions = !!(onSave || onToggle || onDelete);

  const visible = useMemo(() => {
    const needle = q.trim().toLowerCase();
    let out = rows.filter((r) => {
      if (needle && !searchKeys.some((k) => String(valueOf(r, k) ?? '').toLowerCase().includes(needle))) return false;
      return filters.every((f) => !filterVals[f.key] || String(valueOf(r, f.key)) === String(filterVals[f.key]));
    });
    const sk = sort.key;
    if (sk) {
      out = [...out].sort((a, b) => {
        const x = valueOf(a, sk), y = valueOf(b, sk);
        if (typeof x === 'number' && typeof y === 'number') return (x - y) * sort.dir;
        return String(x ?? '').localeCompare(String(y ?? ''), undefined, { numeric: true }) * sort.dir;
      });
    }
    return out;
  }, [rows, q, filterVals, sort, searchKeys, filters]);

  const pages = Math.max(1, Math.ceil(visible.length / pageSize));
  const cur = Math.min(page, pages);
  const slice = visible.slice((cur - 1) * pageSize, cur * pageSize);

  const toggleSort = (key: string) => setSort((s) => (s.key === key ? { key, dir: s.dir === 1 ? -1 : 1 } : { key, dir: 1 }));
  const openNew = () => { setForm(Object.fromEntries(fields.map((f) => [f.key, f.default ?? '']))); setErr(''); setEditing({ row: null }); };
  const openEdit = (row: R) => { setForm(toForm ? toForm(row) : { ...(row as unknown as FormValues) }); setErr(''); setEditing({ row }); };
  const close = () => setEditing(null);

  const save = async (e: React.FormEvent) => {
    e.preventDefault(); setErr('');
    if (!onSave) return;
    // String([]) === '' so an empty checkbox list counts as missing too.
    const missing = drawerFields
      .filter((f) => f.required && !(f.optionalOnEdit && isEdit) && String(form[f.key] ?? '').trim() === '')
      .map((f) => f.label);
    if (missing.length) { setErr(`Please fill: ${missing.join(', ')}`); return; }
    const custom = validate ? validate(form, editing?.row ?? null) : null;
    if (custom) { setErr(custom); return; }
    setSaving(true);
    try {
      await onSave(form, editing?.row ?? null);
      setNotice({ tone: 'ok', text: isEdit ? `${addLabel} updated` : `${addLabel} added` });
      close();
    }
    catch (e2) { setErr(errorText(e2)); }
    finally { setSaving(false); }
  };

  const exportCsv = () => {
    const cols = columns.filter((c) => c.csv !== false);
    const esc = (v: unknown) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const lines = [cols.map((c) => esc(c.label)).join(','),
      ...visible.map((r) => cols.map((c) => esc(c.csvValue ? c.csvValue(r) : valueOf(r, c.key))).join(','))];
    const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `${title.toLowerCase().replace(/\s+/g, '-')}.csv`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 1000);   // revoking immediately can cancel the download
  };

  const doDelete = async () => {
    if (!confirmDel || !onDelete) return;
    setDeleting(true);
    try {
      await onDelete(confirmDel);
      setNotice({ tone: 'ok', text: `${addLabel} deleted` });
      setConfirmDel(null);
    } catch (e) {
      setNotice({ tone: 'err', text: errorText(e) });
      setConfirmDel(null);
    } finally { setDeleting(false); }
  };

  const cell = (c: Column<R>, r: R): React.ReactNode => (c.render ? c.render(r) : String(valueOf(r, c.key) ?? '—'));
  const rowId = (r: R) => String(valueOf(r, idKey));
  const setField = (k: string, v: FieldValue) => setForm((prev) => ({ ...prev, [k]: v }));
  const toggleIn = (k: string, v: string) => setForm((prev) => {
    const cur = Array.isArray(prev[k]) ? (prev[k] as string[]) : [];
    return { ...prev, [k]: cur.includes(v) ? cur.filter((x) => x !== v) : [...cur, v] };
  });

  const actions = (r: R) => (
    <>
      {onToggle && (
        <button type="button" style={S.act} onClick={() => onToggle(r)}
          title={valueOf(r, statusKey) ? 'Deactivate' : 'Activate'}>{valueOf(r, statusKey) ? '⏸' : '▶'}</button>
      )}
      {editable(r) && (
        <button type="button" style={S.act} onClick={() => openEdit(r)} title="Edit" aria-label="Edit">
          <Icon name="edit" size={14} stroke={2} />
        </button>
      )}
      {deletable(r) && (
        <button type="button" style={{ ...S.act, color: 'var(--fx-red)' }} onClick={() => setConfirmDel(r)} title="Delete" aria-label="Delete">
          <Icon name="trash" size={14} stroke={2} />
        </button>
      )}
    </>
  );

  return (
    <div style={P.page}>
      {/* Header */}
      <div style={P.head}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={S.titleIcon}><Icon name={icon} size={18} stroke={2} /></div>
          <div>
            <h1 style={P.h1}>{title}</h1>
            <p style={P.sub}>{subtitle}</p>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button type="button" style={P.btnGhost} onClick={exportCsv}>⬇ Export</button>
          {onSave && <button type="button" style={P.btnPrimary} onClick={openNew}>＋ {addLabel}</button>}
        </div>
      </div>

      {banner && <div style={S.banner}>{banner}</div>}

      {sample && !banner && (
        <div style={S.sample}>
          <b>Sample data.</b> This screen shows placeholder records so the layout can be reviewed.
          The original master data will be imported here.
        </div>
      )}

      {notice && (
        <div role="status" style={{ ...S.notice, ...(notice.tone === 'err' ? S.noticeErr : S.noticeOk) }}>
          <Icon name={notice.tone === 'err' ? 'close' : 'check'} size={14} stroke={2.4} />
          <span style={{ flex: 1 }}>{notice.text}</span>
          <button type="button" style={S.noticeX} onClick={() => setNotice(null)} aria-label="Dismiss">✕</button>
        </div>
      )}

      {/* Stats */}
      {stats.length > 0 && (
        <div style={{ ...S.stats, gridTemplateColumns: isMobile ? '1fr 1fr' : `repeat(${stats.length}, 1fr)` }}>
          {stats.map((s) => (
            <div key={s.label} style={S.stat}>
              <span style={{ ...S.statDot, background: s.tint }} />
              <div>
                <div style={S.statVal}>{s.value}</div>
                <div style={S.statLbl}>{s.label}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div style={P.card}>
        {/* Toolbar */}
        <div style={S.toolbar}>
          <div style={S.searchWrap}>
            <span style={S.searchIcon}><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="7"/><path d="m20 20-4-4"/></svg></span>
            <input style={S.search} value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }}
              placeholder={`Search ${title.toLowerCase()}…`} />
          </div>
          {filters.map((f) => (
            <select key={f.key} style={S.select} value={filterVals[f.key] || ''}
              onChange={(e) => { setFilterVals((v) => ({ ...v, [f.key]: e.target.value })); setPage(1); }}>
              <option value="">{f.label}: All</option>
              {(f.options || []).map((o) => <option key={optValue(o)} value={optValue(o)}>{optLabel(o)}</option>)}
            </select>
          ))}
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: 'var(--fx-text-2)' }}>
            Show
            <select style={{ ...S.select, minWidth: 0 }} value={pageSize} onChange={(e) => { setPageSize(+e.target.value); setPage(1); }}>
              {PAGE_SIZES.map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
        </div>

        {/* Table (desktop) / cards (mobile) */}
        {isMobile ? (
          <div style={{ padding: 12, display: 'grid', gap: 10 }}>
            {slice.map((r) => (
              <div key={rowId(r)} style={S.mCard}>
                {columns.map((c) => (
                  <div key={c.key} style={S.mRow}>
                    <span style={S.mLbl}>{c.label}</span>
                    <span style={S.mVal}>{cell(c, r)}</span>
                  </div>
                ))}
                {(editable(r) || deletable(r) || onToggle) && (
                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6, marginTop: 8 }}>{actions(r)}</div>
                )}
              </div>
            ))}
            {slice.length === 0 && <div style={P.emptyTd}>{emptyText}</div>}
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={P.table}>
              <thead>
                <tr>
                  <th style={{ ...P.th, width: 56 }}>#</th>
                  {columns.map((c) => (
                    <th key={c.key} style={{ ...P.th, cursor: c.sortable === false ? 'default' : 'pointer', width: c.width }}
                      onClick={() => c.sortable !== false && toggleSort(c.key)}>
                      {c.label}{sort.key === c.key ? (sort.dir === 1 ? ' ↑' : ' ↓') : ''}
                    </th>
                  ))}
                  {hasActions && <th style={{ ...P.th, textAlign: 'right' }}>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {slice.map((r, i) => (
                  <tr key={rowId(r)} style={S.tr}>
                    <td style={{ ...P.td, color: 'var(--fx-text-3)' }}>{(cur - 1) * pageSize + i + 1}</td>
                    {columns.map((c) => <td key={c.key} style={P.td}>{cell(c, r)}</td>)}
                    {hasActions && <td style={{ ...P.td, textAlign: 'right', whiteSpace: 'nowrap' }}>{actions(r)}</td>}
                  </tr>
                ))}
                {slice.length === 0 && (
                  <tr><td colSpan={columns.length + 2} style={P.emptyTd}>{emptyText}</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        <div style={S.foot}>
          <span style={{ fontSize: 12, color: 'var(--fx-text-2)' }}>
            {visible.length === 0 ? 'No entries' : `Showing ${(cur - 1) * pageSize + 1}–${Math.min(cur * pageSize, visible.length)} of ${visible.length}`}
          </span>
          <div style={{ display: 'flex', gap: 4 }}>
            <button type="button" style={S.pg} disabled={cur === 1} onClick={() => setPage(cur - 1)}>‹</button>
            {pageList(cur, pages).map((p, i) => p === '…'
              ? <span key={`e${i}`} style={{ ...S.pg, border: 'none', cursor: 'default' }}>…</span>
              : <button type="button" key={p} style={{ ...S.pg, ...(p === cur ? S.pgOn : {}) }} onClick={() => setPage(p)}>{p}</button>)}
            <button type="button" style={S.pg} disabled={cur === pages} onClick={() => setPage(cur + 1)}>›</button>
          </div>
        </div>
      </div>

      {/* Add / Edit drawer */}
      {editing && (
        <>
          <div style={S.scrim} onClick={close} />
          <form onSubmit={save} style={{ ...S.drawer, width: isMobile ? 'calc(100% - 20px)' : drawerWidth }}>
            <div style={S.dHead}>
              <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--fx-text)' }}>
                {isEdit ? `Edit ${addLabel}` : `New ${addLabel}`}
              </div>
              <button type="button" style={S.x} onClick={close}>✕</button>
            </div>
            <div style={{ ...S.dBody, ...S.dGrid }}>
              {drawerFields.map((f) => (
                <div key={f.key} style={{ ...P.field, gridColumn: f.half && !isMobile ? 'span 1' : '1 / -1' }}>
                  <label style={P.label}>{f.label}{f.required && !(f.optionalOnEdit && isEdit) ? ' *' : ''}</label>
                  {f.type === 'checkboxes' ? (
                    <div style={S.checks}>
                      {(f.options || []).map((o) => {
                        const v = optValue(o);
                        const on = Array.isArray(form[f.key]) && (form[f.key] as string[]).includes(v);
                        const hint = typeof o === 'string' ? undefined : o.hint;
                        return (
                          <label key={v} style={{ ...S.check, ...(on ? S.checkOn : {}) }}>
                            <input type="checkbox" checked={on} onChange={() => toggleIn(f.key, v)} style={S.checkInput} />
                            <span style={{ ...S.checkBox, ...(on ? S.checkBoxOn : {}) }}>{on && <Icon name="check" size={11} stroke={3} />}</span>
                            <span style={{ minWidth: 0 }}>
                              <span style={S.checkLbl}>{optLabel(o)}</span>
                              {hint && <span style={S.checkHint}>{hint}</span>}
                            </span>
                          </label>
                        );
                      })}
                      {(f.options || []).length === 0 && <div style={S.hint}>Nothing to choose from yet.</div>}
                    </div>
                  ) : f.type === 'select' ? (
                    <select style={P.input} value={String(form[f.key] ?? '')} onChange={(e) => setField(f.key, e.target.value)}>
                      <option value="">Choose…</option>
                      {(f.options || []).map((o) => <option key={optValue(o)} value={optValue(o)}>{optLabel(o)}</option>)}
                    </select>
                  ) : f.type === 'toggle' ? (
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--fx-text)' }}>
                      <input type="checkbox" checked={!!form[f.key]} onChange={(e) => setField(f.key, e.target.checked)} />
                      {form[f.key] ? (f.onLabel || 'Active') : (f.offLabel || 'Inactive')}
                    </label>
                  ) : (
                    <input style={P.input} type={f.type || 'text'} value={String(form[f.key] ?? '')} placeholder={f.placeholder}
                      disabled={!!f.lockOnEdit && isEdit}
                      autoComplete={f.type === 'password' ? 'new-password' : f.type === 'email' ? 'off' : undefined}
                      onChange={(e) => setField(f.key, e.target.value)} />
                  )}
                  {f.hint && <div style={S.hint}>{f.hint}</div>}
                </div>
              ))}
              {err && <div style={{ ...S.err, gridColumn: '1 / -1' }}>{err}</div>}
            </div>
            <div style={S.dFoot}>
              <button type="button" style={P.btnGhost} onClick={close}>Cancel</button>
              <button style={{ ...P.btnPrimary, opacity: saving ? 0.7 : 1 }} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
            </div>
          </form>
        </>
      )}

      {/* Delete confirmation */}
      {confirmDel && (
        <>
          <div style={S.scrim} onClick={() => !deleting && setConfirmDel(null)} />
          <div role="alertdialog" aria-modal="true" style={S.dialog}>
            <div style={S.dialogIcon}><Icon name="trash" size={20} stroke={2} /></div>
            <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--fx-text)', marginBottom: 6 }}>Delete {addLabel.toLowerCase()}?</div>
            <div style={{ fontSize: 12.5, color: 'var(--fx-text-2)', lineHeight: 1.5 }}>
              <b style={{ color: 'var(--fx-text)' }}>{rowLabel ? rowLabel(confirmDel) : rowId(confirmDel)}</b> will be removed. This cannot be undone.
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 18 }}>
              <button type="button" style={{ ...P.btnGhost, flex: 1 }} disabled={deleting} onClick={() => setConfirmDel(null)}>Cancel</button>
              <button type="button" style={{ ...P.btnPrimary, ...S.btnDanger, flex: 1, opacity: deleting ? 0.7 : 1 }} disabled={deleting} onClick={doDelete}>
                {deleting ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// 1 … 4 5 6 … 20
function pageList(cur: number, total: number): (number | '…')[] {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const out: (number | '…')[] = [1];
  if (cur > 3) out.push('…');
  for (let p = Math.max(2, cur - 1); p <= Math.min(total - 1, cur + 1); p++) out.push(p);
  if (cur < total - 2) out.push('…');
  out.push(total);
  return out;
}

// Small reusable badge for Active/Inactive and similar states.
export function Pill({ on, onText = 'Active', offText = 'Inactive' }: { on: boolean; onText?: string; offText?: string }) {
  return <span style={{ ...S.pill, ...(on ? S.pillOn : S.pillOff) }}>{on ? onText : offText}</span>;
}

const S = css({
  titleIcon: { width: 38, height: 38, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', background: 'linear-gradient(180deg, #4DA3FF 0%, #007AFF 100%)', boxShadow: '0 2px 6px rgba(0,122,255,0.3), inset 0 1px 0 rgba(255,255,255,0.3)' },
  banner: { background: 'var(--fx-gray-soft)', border: '1px solid var(--fx-border)', color: 'var(--fx-text-2)', fontSize: 12, padding: '9px 13px', borderRadius: 10, marginBottom: 14, lineHeight: 1.6 },
  sample: { background: 'var(--fx-orange-soft)', border: '1px solid var(--fx-border)', color: 'var(--fx-text)', fontSize: 12, padding: '9px 13px', borderRadius: 10, marginBottom: 14, lineHeight: 1.5 },
  stats: { display: 'grid', gap: 10, marginBottom: 14 },
  stat: { display: 'flex', alignItems: 'center', gap: 11, background: 'var(--fx-surface)', border: '1px solid var(--fx-border)', borderRadius: 12, padding: '12px 14px', boxShadow: 'var(--fx-shadow)' },
  statDot: { width: 4, height: 30, borderRadius: 4 },
  statVal: { fontSize: 20, fontWeight: 700, letterSpacing: '-0.02em', color: 'var(--fx-text)', lineHeight: 1.1 },
  statLbl: { fontSize: 11.5, color: 'var(--fx-text-2)', marginTop: 2 },
  toolbar: { display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, padding: '12px 14px', borderBottom: '1px solid var(--fx-border)' },
  searchWrap: { position: 'relative', flex: '1 1 220px', maxWidth: 320 },
  searchIcon: { position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--fx-text-3)', display: 'flex' },
  search: { width: '100%', padding: '7px 11px 7px 30px', borderRadius: 8, border: '1px solid transparent', background: 'var(--fx-gray-soft)', color: 'var(--fx-text)', fontSize: 12.5, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' },
  select: { padding: '6px 9px', borderRadius: 8, border: '1px solid var(--fx-border-strong)', fontSize: 12, background: 'var(--fx-input)', color: 'var(--fx-text)', minWidth: 140, fontFamily: 'inherit' },
  tr: { transition: 'background 0.12s' },
  act: { background: 'var(--fx-gray-soft)', border: 'none', color: 'var(--fx-accent)', width: 28, height: 28, borderRadius: 7, cursor: 'pointer', marginLeft: 5, fontSize: 13 },
  foot: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10, padding: '10px 14px' },
  pg: { minWidth: 28, height: 28, padding: '0 7px', borderRadius: 7, border: 'none', background: 'transparent', color: 'var(--fx-text)', fontSize: 12, fontWeight: 500, cursor: 'pointer' },
  pgOn: { background: 'var(--fx-accent)', color: '#fff', fontWeight: 600 },
  mCard: { border: '1px solid var(--fx-border)', borderRadius: 12, padding: 12, background: 'var(--fx-surface)' },
  mRow: { display: 'flex', justifyContent: 'space-between', gap: 10, padding: '3px 0', fontSize: 12.5 },
  mLbl: { color: 'var(--fx-text-3)' },
  mVal: { color: 'var(--fx-text)', fontWeight: 500, textAlign: 'right' },
  scrim: { position: 'fixed', inset: 0, background: 'var(--fx-scrim)', backdropFilter: 'blur(2px)', WebkitBackdropFilter: 'blur(2px)', zIndex: 80 },
  drawer: { position: 'fixed', top: 10, right: 10, bottom: 10, zIndex: 81, borderRadius: 14, background: 'var(--fx-surface)', border: '1px solid var(--fx-border)', display: 'flex', flexDirection: 'column', boxShadow: 'var(--fx-shadow-lg)', overflow: 'hidden' },
  dHead: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 18px', borderBottom: '1px solid var(--fx-border)' },
  x: { background: 'var(--fx-gray-soft)', border: 'none', width: 26, height: 26, borderRadius: '50%', fontSize: 12, cursor: 'pointer', color: 'var(--fx-text-2)' },
  dBody: { flex: 1, overflow: 'auto', padding: 18 },
  dFoot: { display: 'flex', justifyContent: 'flex-end', gap: 8, padding: '12px 18px', borderTop: '1px solid var(--fx-border)' },
  hint: { fontSize: 11, color: 'var(--fx-text-3)', marginTop: 4 },
  err: { color: 'var(--fx-red)', fontSize: 12.5, marginTop: 4 },
  pill: { fontSize: 11, fontWeight: 600, padding: '2px 9px', borderRadius: 20, display: 'inline-block' },
  pillOn: { background: 'var(--fx-green-soft)', color: 'var(--fx-green)' },
  pillOff: { background: 'var(--fx-gray-soft)', color: 'var(--fx-text-2)' },
  dGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: 12, alignContent: 'start' },
  checks: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 8 },
  check: { position: 'relative', display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 11px', borderRadius: 10, border: '1px solid var(--fx-border-strong)', background: 'var(--fx-input)', cursor: 'pointer', transition: 'border-color .15s, background .15s' },
  checkOn: { borderColor: 'var(--fx-accent)', background: 'var(--fx-accent-soft)' },
  checkInput: { position: 'absolute', opacity: 0, width: 1, height: 1, margin: 0 },
  checkBox: { flex: 'none', width: 17, height: 17, marginTop: 1, borderRadius: 5, border: '1.5px solid var(--fx-border-strong)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', background: 'var(--fx-surface)' },
  checkBoxOn: { background: 'var(--fx-accent)', borderColor: 'var(--fx-accent)' },
  checkLbl: { display: 'block', fontSize: 12.5, fontWeight: 600, color: 'var(--fx-text)' },
  checkHint: { display: 'block', fontSize: 11, color: 'var(--fx-text-3)', marginTop: 2, lineHeight: 1.35 },
  notice: { display: 'flex', alignItems: 'center', gap: 9, fontSize: 12.5, padding: '9px 12px', borderRadius: 10, marginBottom: 14, border: '1px solid var(--fx-border)' },
  noticeOk: { background: 'var(--fx-green-soft)', color: 'var(--fx-green)' },
  noticeErr: { background: 'var(--fx-red-soft)', color: 'var(--fx-red)' },
  noticeX: { background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', fontSize: 12, opacity: 0.7 },
  dialog: { position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 82, width: 'min(340px, calc(100% - 32px))', background: 'var(--fx-surface)', border: '1px solid var(--fx-border)', borderRadius: 16, padding: 22, boxShadow: 'var(--fx-shadow-lg)', textAlign: 'center' },
  dialogIcon: { width: 44, height: 44, borderRadius: '50%', margin: '0 auto 12px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--fx-red-soft)', color: 'var(--fx-red)' },
  btnDanger: { background: 'var(--fx-red)', borderColor: 'var(--fx-red)' },
});
